import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ProjectsState {
    data: {
        tasks: Record<string, unknown>[];
        members: Record<string, unknown>[];
    };
    singleProject: Record<string, unknown>[];
    loading: boolean;
    projectAnalysisLoading: boolean;
    projectAnalysisError: string | null;
    error: string | null;
    projectAnalysisData: Record<string, unknown>;
    mappedResources: Record<string, unknown>;
    mappedResourcesLoading: boolean;
    mappedResourcesError: string | null;
    saveMappedResourcesLoading: boolean;
    saveMappedResourcesError: string | null;
    simulationMergeLoading: boolean;
    simulationMergeError: string | null;
    taskList: Record<string, unknown>[];
    taskListError: string | null;
    taskListLoading: boolean;
    versions: Record<string, unknown>[];
    versionsError: string | null;
    versionsLoading: boolean;
    syncWithJiraError: string | null;
    syncWithJiraLoading: boolean;
    syncWithAzureError: string | null;
    syncWithAzureLoading: boolean;
    projectHistory: Record<string, unknown>[];
    projectHistoryError: string | null;
    projectHistoryLoading: boolean;
    projectIntegrations: Record<string, unknown>[];
    projectIntegrationsError: string | null;
    projectIntegrationsLoading: boolean;
    projectStatsData: Record<string, unknown>;
    projectStatsError: string | null;
    projectStatsLoading: boolean;
    projectShareData: Record<string, unknown>;
    projectShareError: string | null;
    projectShareLoading: boolean;
    singleProjectData: Record<string, unknown>;
    singleProjectError: string | null;
    singleProjectLoading: boolean;
    notificationSettingLoading: boolean;
    notificationSettingError: string | null;
    notificationSettingData: Record<string, unknown>;
    hasSimulationTasks: boolean;
    task: Record<string, unknown>;
    taskLoading: boolean;
    taskError: string | null;
    addResourcesLoading: boolean;
    addResourcesError: string | null;
    updateSimulationLoading: boolean;
    updateSimulationError: string | null;
}

const initialState: ProjectsState = {
    data: {
        tasks: [],
        members: [],
    },
    singleProject: [],
    loading: false,
    projectAnalysisLoading: false,
    projectAnalysisError: null,
    error: null,
    projectAnalysisData: {},
    mappedResources: {},
    mappedResourcesLoading: false,
    mappedResourcesError: null,
    saveMappedResourcesLoading: false,
    saveMappedResourcesError: null,
    simulationMergeLoading: false,
    simulationMergeError: null,
    taskList: [],
    taskListError: null,
    taskListLoading: false,
    versions: [],
    versionsError: null,
    versionsLoading: false,
    syncWithJiraError: null,
    syncWithJiraLoading: false,
    syncWithAzureError: null,
    syncWithAzureLoading: false,
    projectHistory: [],
    projectHistoryError: null,
    projectHistoryLoading: false,
    projectIntegrations: [],
    projectIntegrationsError: null,
    projectIntegrationsLoading: false,
    projectStatsData: {},
    projectStatsError: null,
    projectStatsLoading: false,
    projectShareData: {},
    projectShareError: null,
    projectShareLoading: false,
    singleProjectData: {},
    singleProjectError: null,
    singleProjectLoading: false,
    notificationSettingLoading: false,
    notificationSettingError: null,
    notificationSettingData: {},
    hasSimulationTasks: false,
    task: {},
    taskLoading: false,
    taskError: null,
    addResourcesLoading: false,
    addResourcesError: null,
    updateSimulationLoading: false,
    updateSimulationError: null,
};

export const projectsSlice = createSlice({
    name: 'projects',
    initialState,

    reducers: {
        setProjects: (state, action: PayloadAction<Record<string, unknown>>) => {
            const project = action.payload;
            state.data = project as ProjectsState['data'];
        },
        setSingleProject: (state, action: PayloadAction<Record<string, unknown>>) => {
            const project = action.payload;
            state.singleProject.push(project);
        },
        setProjectsLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setProjectsError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setTasksLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setTasksError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setTask: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.task = action.payload;
        },
        resetTaskData: (state) => {
            state.task = {};
        },
        setTaskLoading: (state, action: PayloadAction<boolean>) => {
            state.taskLoading = action.payload;
        },
        setTaskError: (state, action: PayloadAction<string | null>) => {
            state.taskError = action.payload;
        },
        setAddResourcesLoading: (state, action: PayloadAction<boolean>) => {
            state.addResourcesLoading = action.payload;
        },
        setAddResourcesError: (state, action: PayloadAction<string | null>) => {
            state.addResourcesError = action.payload;
        },
        setProjectAnalysisLoading: (state, action: PayloadAction<boolean>) => {
            state.projectAnalysisLoading = action.payload;
        },
        setProjectAnalysisError: (state, action: PayloadAction<string | null>) => {
            state.projectAnalysisError = action.payload;
        },
        setProjectAnalysisData: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.projectAnalysisData = action.payload;
        },
        setMappedResourcesLoading: (state, action: PayloadAction<boolean>) => {
            state.mappedResourcesLoading = action.payload;
        },
        setMappedResourcesError: (state, action: PayloadAction<string | null>) => {
            state.mappedResourcesError = action.payload;
        },
        setMappedResources: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.mappedResources = action.payload;
        },
        setSaveMappedResourcesLoading: (state, action: PayloadAction<boolean>) => {
            state.saveMappedResourcesLoading = action.payload;
        },
        setSaveMappedResourcesError: (state, action: PayloadAction<string | null>) => {
            state.saveMappedResourcesError = action.payload;
        },
        setSimulationMergeLoading: (state, action: PayloadAction<boolean>) => {
            state.simulationMergeLoading = action.payload;
        },
        setSimulationMergeError: (state, action: PayloadAction<string | null>) => {
            state.simulationMergeError = action.payload;
        },
        setTaskList: (state, action: PayloadAction<{ tasks: Record<string, unknown>[] }>) => {
            state.taskList = action.payload.tasks;
        },
        setTaskListError: (state, action: PayloadAction<string | null>) => {
            state.taskListError = action.payload;
        },
        setTaskListLoading: (state, action: PayloadAction<boolean>) => {
            state.taskListLoading = action.payload;
        },
        setVersions: (state, action: PayloadAction<{ versions: Record<string, unknown>[] }>) => {
            state.versions = action.payload.versions;
        },
        setVersionsError: (state, action: PayloadAction<string | null>) => {
            state.versionsError = action.payload;
        },
        setVersionsLoading: (state, action: PayloadAction<boolean>) => {
            state.versionsLoading = action.payload;
        },
        setSyncWithJiraError: (state, action: PayloadAction<string | null>) => {
            state.syncWithJiraError = action.payload;
        },
        setSyncWithJiraLoading: (state, action: PayloadAction<boolean>) => {
            state.syncWithJiraLoading = action.payload;
        },
        setUpdateSimulationError: (state, action: PayloadAction<string | null>) => {
            state.updateSimulationError = action.payload;
        },
        setUpdateSimulationLoading: (state, action: PayloadAction<boolean>) => {
            state.updateSimulationLoading = action.payload;
        },
        setProjectHistory: (state, action: PayloadAction<{ history?: Record<string, unknown>[] }>) => {
            state.projectHistory = action?.payload?.history ?? [];
        },
        setProjectHistoryError: (state, action: PayloadAction<string | null>) => {
            state.projectHistoryError = action.payload;
        },
        setProjectHistoryLoading: (state, action: PayloadAction<boolean>) => {
            state.projectHistoryLoading = action.payload;
        },
        setProjectIntegrations: (state, action: PayloadAction<{ integrations?: Record<string, unknown>[] }>) => {
            state.projectIntegrations = action?.payload?.integrations ?? [];
        },
        setProjectIntegrationsError: (state, action: PayloadAction<string | null>) => {
            state.projectIntegrationsError = action.payload;
        },
        setProjectIntegrationsLoading: (state, action: PayloadAction<boolean>) => {
            state.projectIntegrationsLoading = action.payload;
        },
        setProjectStats: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.projectStatsData = action?.payload;
        },
        setProjectStatsError: (state, action: PayloadAction<string | null>) => {
            state.projectStatsError = action.payload;
        },
        setProjectStatsLoading: (state, action: PayloadAction<boolean>) => {
            state.projectStatsLoading = action.payload;
        },
        setProjectShare: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.projectShareData = action?.payload;
        },
        setProjectShareError: (state, action: PayloadAction<string | null>) => {
            state.projectShareError = action.payload;
        },
        setProjectShareLoading: (state, action: PayloadAction<boolean>) => {
            state.projectShareLoading = action.payload;
        },
        setSingleProjectData: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.singleProjectData = action?.payload;
        },
        setSingleProjectError: (state, action: PayloadAction<string | null>) => {
            state.singleProjectError = action.payload;
        },
        setSingleProjectLoading: (state, action: PayloadAction<boolean>) => {
            state.singleProjectLoading = action.payload;
        },
        setNotificationSettingLoading: (state, action: PayloadAction<boolean>) => {
            state.notificationSettingLoading = action.payload;
        },
        setNotificationSettingError: (state, action: PayloadAction<string | null>) => {
            state.notificationSettingError = action.payload;
        },
        setNotificationSettingData: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.singleProjectData = action?.payload;
        },
        setNotificationSetting: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.notificationSettingData = action?.payload;
        },
        setHasSimulationTasks: (state, action: PayloadAction<boolean>) => {
            state.hasSimulationTasks = action.payload;
        },
    },
});

export const {
    setProjects, setProjectsLoading, setProjectsError,
    setTasksLoading, setTasksError, setTask, resetTaskData,
    setTaskLoading, setTaskError, setAddResourcesLoading,
    setAddResourcesError,
    setProjectAnalysisLoading,
    setProjectAnalysisError,
    setProjectAnalysisData,
    setMappedResourcesLoading,
    setMappedResourcesError,
    setMappedResources,
    setSaveMappedResourcesLoading,
    setSaveMappedResourcesError,
    setSimulationMergeLoading,
    setSimulationMergeError,
    setTaskList, setTaskListError, setTaskListLoading,
    setVersions, setVersionsError, setVersionsLoading,
    setSyncWithJiraLoading, setSyncWithJiraError,
    setUpdateSimulationLoading, setUpdateSimulationError,
    setProjectHistoryLoading, setProjectHistoryError,
    setProjectHistory,
    setProjectIntegrationsLoading, setProjectIntegrationsError,
    setProjectIntegrations,
    setProjectStatsLoading, setProjectStats, setProjectStatsError,
    setProjectShareLoading, setProjectShare, setProjectShareError,
    setSingleProjectLoading, setSingleProjectData, setSingleProjectError,
    setNotificationSettingError, setNotificationSettingLoading,
    setNotificationSetting,
    setHasSimulationTasks,
} = projectsSlice.actions;

export default projectsSlice.reducer;
